import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Brain, Check, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("pending"); // pending | verified | invalid
  const [detail, setDetail] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setDetail("No verification token in the link.");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then(({ data }) => {
        setStatus(data.verified ? "verified" : "invalid");
        setDetail(data.detail || "");
      })
      .catch((e) => {
        setStatus("invalid");
        setDetail(e.response?.data?.detail || "Verification failed.");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-appbg text-white flex flex-col items-center justify-center px-6" data-testid="verify-email-page">
      <Link to="/" className="flex items-center gap-2 mb-12">
        <Brain className="text-blaze" size={22} />
        <span className="heading text-2xl">CoachConnect <span className="text-blaze">AI</span></span>
      </Link>

      <div className="max-w-md w-full bg-appcard border border-neutral-800 p-10 text-center">
        {status === "pending" && (
          <div data-testid="verify-pending">
            <Loader2 className="mx-auto text-volt animate-spin" size={48} />
            <div className="heading text-3xl mt-6">CONFIRMING YOUR EMAIL...</div>
            <p className="text-neutral-400 mt-3 text-sm">This should take just a moment.</p>
          </div>
        )}

        {status === "verified" && (
          <div data-testid="verify-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-blaze flex items-center justify-center">
              <Check className="text-white" size={32} />
            </div>
            <div className="heading text-3xl mt-6">EMAIL CONFIRMED.</div>
            <p className="text-neutral-400 mt-3 text-sm">You&apos;re all set. Match alerts and program invites will now reach you.</p>
            <Link
              to="/coach"
              data-testid="verify-goto-dashboard"
              className="mt-8 inline-flex items-center gap-2 bg-blaze text-white heading text-sm px-5 py-3 hover:glow-blaze transition-all"
            >
              GO TO DASHBOARD →
            </Link>
          </div>
        )}

        {status === "invalid" && (
          <div data-testid="verify-invalid">
            <div className="mx-auto w-16 h-16 rounded-full bg-neutral-800 flex items-center justify-center">
              <X className="text-neutral-400" size={32} />
            </div>
            <div className="heading text-3xl mt-6">LINK EXPIRED</div>
            <p className="text-neutral-400 mt-3 text-sm">{detail || "This verification link is no longer valid."}</p>
            <form
              className="mt-6 flex flex-col gap-2"
              data-testid="verify-resend-form"
              onSubmit={async (e) => {
                e.preventDefault();
                setResendBusy(true);
                try {
                  const { data } = await api.post("/auth/resend-verification", { email: resendEmail });
                  setResendMsg(data.detail || "If an unverified account exists for that email, a fresh link is on its way.");
                } catch {
                  setResendMsg("If an unverified account exists for that email, a fresh link is on its way.");
                } finally {
                  setResendBusy(false);
                }
              }}
            >
              <input
                type="email" required value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                placeholder="you@school.edu" data-testid="verify-resend-email"
                className="bg-appbg border border-neutral-700 px-3 py-2 text-sm text-white"
              />
              <button type="submit" disabled={resendBusy} data-testid="verify-resend-submit"
                className="bg-blaze text-white heading text-sm px-5 py-3 hover:glow-blaze transition-all disabled:opacity-50">
                {resendBusy ? "SENDING…" : "SEND ME A FRESH LINK"}
              </button>
              {resendMsg && <div className="text-xs text-neutral-400" data-testid="verify-resend-msg">{resendMsg}</div>}
            </form>
            <Link
              to="/auth?mode=login"
              data-testid="verify-goto-login"
              className="mt-6 inline-flex items-center gap-2 bg-neutral-800 text-white heading text-sm px-5 py-3 hover:bg-neutral-700 transition-all"
            >
              SIGN IN →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
